import { drypApi } from "@/services/drypApi";
import { UserStorage } from "@/storage/UserStorage";
import { user, UserOrganizationAccess } from "@/endpoints/dryp/user";

export const auth = {
	login: async ({ email, password }: LoginRequest): Promise<LoginResponse> =>
		await drypApi.post<LoginResponse>("/Account/Login", {
			Email: email,
			Password: password,
		}),
	forgotPassword: async ({ email }: ForgotPasswordRequest): Promise<ForgotPasswordResponse | boolean | undefined> => {
		try {
			return await getForgotPasswordData(email);
		} catch (error) {
			excludeForgotPasswordNotFoundError(error);
		}
	},
	resetPassword: async (payload: ResetPasswordResponse): Promise<boolean | null> =>
		await drypApi.post<boolean | null>("/Profile/ResetPassword", payload),
	getUserAndOrganization: async (): Promise<{
		user: Dryp.User;
		organization: Dryp.Organization;
		organizations: UserOrganizationAccess[];
	} | null> => {
		try {
			const jwtUser = UserStorage.getUser();
			if (!jwtUser) return null;

			const { organizations } = await user.getAuthUser();
			const activeOrg = organizations.find(o => o.organizationId === jwtUser.organizationId);

			return {
				user: { id: jwtUser.id, name: jwtUser.name, roles: jwtUser.roles },
				organization: {
					Id: jwtUser.organizationId,
					Name: activeOrg?.organizationName,
					Description: "",
				},
				organizations,
			};
		} catch {
			return null;
		}
	},
	validateOnboardToken: async (payload: ValidateOnboardTokenRequest): Promise<ValidateOnboardTokenResponse> => {
		return mockValidateOnboardToken(payload.token);
	},
	onboard: async (payload: OnboardRequest): Promise<LoginResponse> => {
		return mockOnboard(payload);
	},
};

async function getForgotPasswordData(email: string): Promise<ForgotPasswordResponse | boolean> {
	return drypApi.post<ForgotPasswordResponse>("/Profile/SendResetPasswordEmail", {
		email,
	});
}

function excludeForgotPasswordNotFoundError(error: unknown) {
	const apiError = error as unknown as ApiError;
	if (apiError?.status !== 404) {
		throw error;
	}
	return true;
}

async function mockValidateOnboardToken(token: string): Promise<ValidateOnboardTokenResponse> {
	return new Promise((resolve, reject) => {
		setTimeout(() => {
			if (token === "test") {
				resolve({
					user: {
						email: "user@example.com",
						firstName: "Tommy",
						lastName: "Kristofen",
					},
					organization: {
						Id: "org-123" as Dryp.OrganizationId,
						Name: "FORS",
						Description: "Company description",
					} as Dryp.Organization,
				});
			} else if (token === "expired-token") {
				reject(new Error("Token expired"));
			} else {
				reject(new Error("Invalid token"));
			}
		}, 500);
	});
}

async function mockOnboard(payload: OnboardRequest): Promise<LoginResponse> {
	console.log("mockOnboard payload", payload);
	return new Promise(resolve => {
		setTimeout(() => {
			resolve({
				Token: "testToken",
				RefreshToken: "refreshToken",
			});
		}, 500);
	});
}

interface OnboardRequest {
	firstName: string;
	lastName?: string;
	token: string;
	password: string;
}

interface ValidateOnboardTokenRequest {
	token: string;
}

export interface ValidateOnboardTokenResponse {
	user: {
		email: string;
		firstName: string;
		lastName?: string;
	};
	organization: Dryp.Organization;
}

interface LoginRequest {
	email: string;
	password: string;
}

interface LoginResponse {
	Token: string;
	RefreshToken: string;
}

interface ResetPasswordResponse {
	userId: Dryp.UserId;
	token: string;
	password: string;
}

interface ForgotPasswordRequest {
	email: string;
}

type ForgotPasswordResponse = boolean | null;
